import { useState, useEffect } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { CourseCard } from "../components/CourseCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Search, Filter, Sparkles } from "lucide-react";

const API = `${window.location.origin}/api`;

const DashboardPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/sessions`);
      // Map sessions to courses format for compatibility
      const mappedSessions = response.data.map(s => ({
        course_id: s.session_id,
        title: s.title,
        description: s.description,
        date: s.date,
        time: s.time,
        level: "Base", // Default level for sessions
        product_line: "ODR standard", // Default product line
        instructor: s.docente_name,
        max_participants: s.max_participants,
        current_participants: s.current_participants
      }));
      setCourses(mappedSessions);
    } catch (error) {
      toast.error("Errore nel caricamento dei corsi");
    } finally {
      setLoading(false);
    }
  };

  const seedData = async () => {
    try {
      await axios.post(`${API}/seed`);
      toast.success("Dati di esempio caricati!");
      fetchCourses();
    } catch (error) {
      toast.info("I dati sono già stati caricati");
    }
  };

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLevel = levelFilter === "all" || course.level === levelFilter;
    const matchesProduct = productFilter === "all" || course.product_line === productFilter;
    return matchesSearch && matchesLevel && matchesProduct;
  });

  return (
    <PageLayout>
      <div data-testid="dashboard-page">
        {/* Hero Section */}
        <section className="hero-gradient py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-foreground mb-4">
                Corsi di Estetica Oncologica
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Formazione professionale per estetiste che desiderano offrire trattamenti sicuri e personalizzati a clienti oncologici.
              </p>

              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Cerca corsi..."
                  value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-6 rounded-full text-base shadow-md border-border/50 bg-white/80 backdrop-blur-sm"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Content */}
      <section className="py-8 md:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-8">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Filter className="w-4 h-4" />
              Filtra per:
            </div>

            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-40 rounded-full" data-testid="level-filter">
                <SelectValue placeholder="Livello" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i livelli</SelectItem>
                <SelectItem value="Base">Base</SelectItem>
                <SelectItem value="Intermedio">Intermedio</SelectItem>
                <SelectItem value="Avanzato">Avanzato</SelectItem>
              </SelectContent>
            </Select>

            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="w-44 rounded-full" data-testid="product-filter">
                <SelectValue placeholder="Linea prodotti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le linee</SelectItem>
                <SelectItem value="ODR standard">ODR standard</SelectItem>
                <SelectItem value="ODR Clean">ODR Clean</SelectItem>
              </SelectContent>
            </Select>

            {(levelFilter !== "all" || productFilter !== "all" || searchTerm) && (
              <Button
                variant="ghost"
                className="rounded-full text-sm"
                onClick={() => {
                  setLevelFilter("all");
                  setProductFilter("all");
                  setSearchTerm("");
                }}
                data-testid="clear-filters-btn"
              >
                Rimuovi filtri
              </Button>
            )}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-96 animate-pulse" />
              ))}
            </div>
          ) : filteredCourses.length > 0 ? (
            <>
              {/* Results Count */}
              <p className="text-sm text-muted-foreground mb-6">
                {filteredCourses.length} {filteredCourses.length === 1 ? "corso trovato" : "corsi trovati"}
              </p>

              {/* Courses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="courses-grid">
                {filteredCourses.map((course) => (
                  <CourseCard key={course.course_id} course={course} />
                ))}
              </div>
            </>
          ) : courses.length === 0 ? (
            /* Empty State - No courses at all */
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-serif text-2xl font-semibold mb-2">Nessun corso disponibile</h2>
              <p className="text-muted-foreground mb-6">Carica i corsi di esempio per iniziare</p>
              <Button onClick={seedData} className="rounded-full btn-shine" data-testid="seed-btn">
                Carica corsi di esempio
              </Button>
            </div>
          ) : (
            /* Empty State - No matching results */
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
                <Search className="w-10 h-10 text-muted-foreground" />
              </div>
              <h2 className="font-serif text-2xl font-semibold mb-2">Nessun risultato</h2>
              <p className="text-muted-foreground">Prova a modificare i filtri di ricerca</p>
            </div>
          )}
        </div>
      </section>
      </div>
    </PageLayout>
  );
};

export default DashboardPage;
